import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Ship, Mail, Inbox, TrendingUp, Users, Loader2, RefreshCw, Phone, MapPin,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/BrandMark";
import { Seo } from "@/components/Seo";
import { AdminOwners } from "@/components/AdminOwners";
import { api } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES = ["new", "contacted", "qualified", "converted", "archived"];
const STATUS_STYLE = {
  new: "bg-gold-500/15 text-gold-600 border-gold-500/40",
  contacted: "bg-blue-500/10 text-blue-500 border-blue-500/30",
  qualified: "bg-purple-500/10 text-purple-500 border-purple-500/30",
  converted: "bg-green-500/10 text-green-600 border-green-500/30",
  archived: "bg-slate-400/10 text-slate-500 border-slate-400/30",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [hideTest, setHideTest] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: l }] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/leads", { params: filter === "all" ? {} : { type: filter } }),
      ]);
      setStats(s);
      setLeads(l);
    } catch (e) {
      toast.error("Could not load dashboard data.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const onLogout = async () => { await logout(); navigate("/admin/login"); };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error("Could not update status.");
    }
  };

  const STAT_CARDS = stats ? [
    { label: "Total Leads", value: stats.total, icon: Inbox },
    { label: "Inspection Requests", value: stats.inspections, icon: Ship },
    { label: "Contact Inquiries", value: stats.contacts, icon: Mail },
    { label: "New / Unactioned", value: stats.new, icon: TrendingUp },
  ] : [];

  return (
    <div className="min-h-screen bg-ivory dark:bg-navy-900" data-testid="admin-dashboard">
      <Seo title="Admin Dashboard — Hussar Marine Concierge" description="HMC lead management and CRM." />

      <header className="bg-navy-900 border-b border-gold-500/15 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandMark className="h-11 w-11" />
            <div>
              <p className="font-serif text-lg text-white leading-tight">HMC Command</p>
              <p className="eyebrow text-gold-400 -mt-0.5">Lead & CRM Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-white/15 text-slate-200 hover:text-gold-400 transition-colors" data-testid="dashboard-refresh" aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </button>
            <span className="hidden sm:block text-sm text-slate-300">{user?.email}</span>
            <button onClick={onLogout} className="inline-flex items-center gap-2 border border-white/15 text-slate-200 hover:text-gold-400 hover:border-gold-500/40 text-sm font-medium px-4 py-2 rounded-full transition-colors" data-testid="dashboard-logout">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {/* stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {STAT_CARDS.map((c) => (
            <div key={c.label} className="bg-white dark:bg-navy-800 border border-gold-500/20 rounded-2xl p-6" data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex items-center justify-between">
                <c.icon className="h-6 w-6 text-gold-500" />
              </div>
              <p className="mt-4 text-3xl font-bold text-navy-900 dark:text-white">{c.value}</p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{c.label}</p>
            </div>
          ))}
        </div>

        <AdminOwners />

        {/* filters */}
        <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {[
              { k: "all", label: "All" },
              { k: "inspection", label: "Inspections" },
              { k: "contact", label: "Inquiries" },
            ].map((t) => (
              <button key={t.k} onClick={() => setFilter(t.k)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
                  filter === t.k ? "bg-navy-900 dark:bg-gold-500 text-white dark:text-navy-900" : "bg-white dark:bg-navy-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-white/10"
                }`} data-testid={`filter-${t.k}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 dark:text-slate-400" data-testid="lead-counts">
              {leads.filter((l) => !l.is_test).length} genuine · {leads.filter((l) => l.is_test).length} test
            </span>
            <button onClick={() => setHideTest((v) => !v)}
              className={`px-4 py-2.5 rounded-full text-sm font-medium transition-colors border ${
                hideTest ? "bg-gold-500 text-navy-900 border-gold-500" : "bg-white dark:bg-navy-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-white/10"
              }`} data-testid="toggle-hide-test">
              {hideTest ? "Showing genuine only" : "Hide test leads"}
            </button>
          </div>
        </div>

        {/* leads */}
        <div className="mt-6">
          {(() => { const visible = leads.filter((l) => !hideTest || !l.is_test); return (
          loading ? (
            <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : visible.length === 0 ? (
            <div className="text-center py-24 bg-white dark:bg-navy-800 rounded-2xl border border-dashed border-slate-300 dark:border-white/10" data-testid="leads-empty">
              <Users className="h-10 w-10 text-slate-300 mx-auto" />
              <p className="mt-4 text-slate-500 dark:text-slate-400">No leads to show yet. New submissions will appear here.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="leads-list">
              {visible.map((l) => (
                <div key={l.id} className={`bg-white dark:bg-navy-800 border rounded-2xl p-6 ${l.is_test ? "border-dashed border-amber-400/60" : "border-gold-500/15"}`} data-testid={`lead-${l.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={`eyebrow px-2.5 py-1 rounded-full border text-[10px] ${l.type === "inspection" ? "border-gold-500/40 text-gold-600" : "border-blue-500/30 text-blue-500"}`}>
                          {l.type === "inspection" ? "Inspection" : "Inquiry"}
                        </span>
                        {l.is_test && (
                          <span className="eyebrow px-2.5 py-1 rounded-full border border-amber-400/70 text-amber-600 bg-amber-400/10 text-[10px]" data-testid={`lead-test-badge-${l.id}`}>
                            Test
                          </span>
                        )}
                        <h3 className="font-serif text-xl text-navy-900 dark:text-white">{l.name}</h3>
                        <span className="text-xs text-slate-400">{new Date(l.created_at).toLocaleString("en-US")}</span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-slate-600 dark:text-slate-300">
                        <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 hover:text-gold-600"><Mail className="h-3.5 w-3.5" /> {l.email}</a>
                        {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 hover:text-gold-600"><Phone className="h-3.5 w-3.5" /> {l.phone}</a>}
                        {l.service_region && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {l.service_region}</span>}
                      </div>

                      {l.type === "inspection" ? (
                        <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
                          <span><strong className="text-slate-500 dark:text-slate-500">Vessel:</strong> {l.vessel_type} · {l.vessel_length}</span>
                          {l.marina_location && <span><strong className="text-slate-500 dark:text-slate-500">Marina:</strong> {l.marina_location}</span>}
                          {l.preferred_plan && <span><strong className="text-slate-500 dark:text-slate-500">Plan:</strong> {l.preferred_plan}</span>}
                          {l.frequency && <span><strong className="text-slate-500 dark:text-slate-500">Frequency:</strong> {l.frequency}</span>}
                        </div>
                      ) : (
                        l.subject && <p className="mt-3 text-sm text-slate-600 dark:text-slate-400"><strong className="text-slate-500">Subject:</strong> {l.subject}</p>
                      )}
                      {l.message && <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 bg-ivory dark:bg-navy-900 rounded-lg p-3 border border-slate-100 dark:border-white/5">{l.message}</p>}
                    </div>

                    <div className="sm:w-44 shrink-0">
                      <span className={`eyebrow inline-block mb-2 px-3 py-1 rounded-full border text-[10px] ${STATUS_STYLE[l.status] || STATUS_STYLE.new}`}>{l.status}</span>
                      <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                        <SelectTrigger className="bg-ivory dark:bg-navy-900" data-testid={`status-select-${l.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
          ); })()}
        </div>
      </div>
    </div>
  );
}
